import {
    Clock,
    Copy,
    EllipsisVertical,
    ExternalLink,
    Heart,
    Link as LinkIcon,
    Pencil,
    Plus,
    ThumbsDown,
    ThumbsUp,
} from "lucide-react";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { PersonAvatar } from "../avatar/PersonAvatar";
import type {
    ContactDetails,
    FamilyMember,
    FuturePlan,
    PreferenceItem,
    ProfileLink,
} from "./data/profile";

export function ProfileSection({
    title,
    editable,
    onEdit,
    children,
    className,
}: {
    title: string;
    editable?: boolean;
    onEdit?: () => void;
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <section className="flex flex-col gap-3">
            <div className="px-3">
                <Separator />
            </div>
            <div className={cn("flex flex-col gap-3 px-3 pb-6", className)}>
                <div className="flex items-center justify-between gap-2">
                    <h3 className="heading-4! text-zinc-900">{title}</h3>
                    {editable && (
                        <EditButton label={`Edit ${title}`} onClick={onEdit} />
                    )}
                </div>
                {children}
            </div>
        </section>
    );
}

export function ProfileParagraph({
    muted,
    children,
}: {
    muted?: boolean;
    children: React.ReactNode;
}) {
    return (
        <p
            className={cn(
                "whitespace-pre-wrap text-sm leading-body-sm",
                muted ? "text-zinc-500" : "text-zinc-900",
            )}
        >
            {children}
        </p>
    );
}

export function FieldList({
    rows,
    missing,
}: {
    rows?: { label: string; value: string }[];
    missing?: string[];
}) {
    return (
        <>
            {rows?.map((row) => (
                <FieldRow key={`${row.label}-${row.value}`} label={row.label}>
                    <p className="whitespace-pre-wrap text-xs text-zinc-900 leading-4">
                        {row.value}
                    </p>
                </FieldRow>
            ))}
            <MissingRow labels={missing} />
        </>
    );
}

export function FieldRow({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className="flex items-start gap-1">
            <p className="w-24 shrink-0 text-2xs text-zinc-500 leading-3">{label}</p>
            <div className="min-w-0 flex-1 break-words">{children}</div>
        </div>
    );
}

export function EligibilityRow({
    eligibilityDetails,
}: {
    eligibilityDetails?: ContactDetails["eligibilityDetails"];
}) {
    const lines = [
        eligibilityDetails?.bin ? `BIN: ${eligibilityDetails.bin}` : undefined,
        eligibilityDetails?.last4
            ? `Last 4-digits: *${eligibilityDetails.last4}`
            : undefined,
    ].filter(Boolean);
    if (!lines.length) return null;

    return (
        <FieldRow label="Eligibility">
            <p className="whitespace-pre-wrap text-xs text-zinc-900 leading-4">
                {lines.join("\n")}
            </p>
        </FieldRow>
    );
}

export function MissingRow({ labels }: { labels?: string[] }) {
    if (!labels?.length) return null;

    return (
        <FieldRow label="Missing">
            <div className="flex flex-wrap gap-1">
                {labels.map((label) => (
                    <Badge key={label} variant="secondary" size="sm">
                        {label}
                    </Badge>
                ))}
            </div>
        </FieldRow>
    );
}

export function EditButton({
    label,
    onClick,
}: {
    label: string;
    onClick?: () => void;
}) {
    return (
        <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            onClick={onClick}
            className="text-zinc-900 opacity-25 hover:bg-transparent hover:text-zinc-900 hover:opacity-100"
        >
            <Pencil />
        </Button>
    );
}

export function IconMenu({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={<Button variant="ghost" size="icon-sm" aria-label={label} />}
            >
                <EllipsisVertical />
            </DropdownMenuTrigger>
            {/* Sized to the items: the default follows the icon trigger and wraps labels. */}
            <DropdownMenuContent align="end" className="w-auto">
                <DropdownMenuGroup>{children}</DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export function PersonRow({
    member,
    href,
    onView,
    onEdit,
    onRemove,
}: {
    member: FamilyMember;
    href?: string;
    onView?: () => void;
    onEdit?: () => void;
    onRemove?: () => void;
}) {
    const isLinked = member.kind === "linked";

    return (
        <div className="flex items-center gap-3">
            <Button
                variant="ghost"
                className="h-auto min-w-0 flex-1 justify-start gap-3 rounded-md px-0 py-1"
                nativeButton={!href}
                render={
                    href
                        ? (props) => (
                              <a
                                  {...props}
                                  href={href}
                                  target="_blank"
                                  rel="noreferrer"
                              />
                          )
                        : undefined
                }
                onClick={onView}
            >
                <PersonAvatar
                    name={member.name}
                    kind="member"
                    id={member.linkedMemberId}
                />
                <div className="min-w-0 flex-1 text-left">
                    <p className="heading-5 text-zinc-900">{member.name}</p>
                    <p className="text-2xs text-zinc-500 leading-3">{member.detail}</p>
                </div>
                {isLinked && <ExternalLink className="size-4 shrink-0 text-zinc-400" />}
            </Button>
            <IconMenu label={`${member.name} actions`}>
                {onEdit && <DropdownMenuItem onClick={onEdit}>Edit</DropdownMenuItem>}
                {onRemove && (
                    <DropdownMenuItem onClick={onRemove}>
                        {isLinked ? "Remove link" : "Delete"}
                    </DropdownMenuItem>
                )}
            </IconMenu>
        </div>
    );
}

export function LinkRow({
    link,
    onEdit,
    onDelete,
}: {
    link: ProfileLink;
    onEdit?: () => void;
    onDelete?: () => void;
}) {
    const href = link.url?.trim();
    const content = (
        <>
            <Avatar className="size-9">
                <AvatarFallback className="bg-blue-100 text-blue-900">
                    <LinkIcon className="size-4" />
                </AvatarFallback>
            </Avatar>
            <p className="heading-5 min-w-0 flex-1 truncate text-zinc-900 underline-offset-4 group-hover:underline">
                {link.title}
            </p>
        </>
    );

    return (
        <div className="flex items-center gap-3">
            {href ? (
                <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex min-w-0 flex-1 items-center gap-3"
                >
                    {content}
                </a>
            ) : (
                <div className="flex min-w-0 flex-1 items-center gap-3">{content}</div>
            )}
            {(onEdit || onDelete) && (
                <IconMenu label={`${link.title} actions`}>
                    {onEdit && (
                        <DropdownMenuItem onClick={onEdit}>Edit</DropdownMenuItem>
                    )}
                    {onDelete && (
                        <DropdownMenuItem onClick={onDelete}>Delete</DropdownMenuItem>
                    )}
                </IconMenu>
            )}
        </div>
    );
}

export function AddRow({
    label,
    onClick,
    menu,
}: {
    label: string;
    onClick?: () => void;
    menu?: { label: string; onClick: () => void }[];
}) {
    const content = (
        <>
            <Avatar className="size-9">
                <AvatarFallback className="bg-zinc-100 text-emerald-800">
                    <Plus className="size-4" />
                </AvatarFallback>
            </Avatar>
            <span className="heading-5 text-zinc-900">{label}</span>
        </>
    );

    if (!menu) {
        return (
            <Button
                variant="ghost"
                className="h-auto w-full justify-start gap-3 rounded-md px-0 py-2"
                onClick={onClick}
            >
                {content}
            </Button>
        );
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={
                    <Button
                        variant="ghost"
                        className="h-auto w-full justify-start gap-3 rounded-md px-0 py-2"
                    />
                }
            >
                {content}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
                <DropdownMenuGroup>
                    {menu.map((item) => (
                        <DropdownMenuItem key={item.label} onClick={item.onClick}>
                            {item.label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export function CategoryBlock({
    title,
    notes,
    rows,
    futurePlan,
    onboardingFuturePlan,
    missing,
    onEdit,
}: {
    title: string;
    notes?: string;
    rows?: { label: string; value: string }[];
    futurePlan?: FuturePlan;
    onboardingFuturePlan?: FuturePlan;
    missing?: string[];
    onEdit?: () => void;
}) {
    return (
        <ProfileSection title={title} editable onEdit={onEdit}>
            {notes && <ProfileParagraph>{notes}</ProfileParagraph>}
            <FieldList rows={rows} missing={missing} />
            {(futurePlan || onboardingFuturePlan) && (
                <div className="flex flex-col gap-3">
                    <p className="heading-6 text-zinc-900">Future plans</p>
                    {futurePlan && <FuturePlanNote plan={futurePlan} />}
                    {onboardingFuturePlan && (
                        <FuturePlanNote plan={onboardingFuturePlan} />
                    )}
                </div>
            )}
        </ProfileSection>
    );
}

function FuturePlanNote({ plan }: { plan: FuturePlan }) {
    return (
        <div className="flex flex-col gap-1.5">
            <ProfileParagraph>{plan.text}</ProfileParagraph>
            {plan.source && (
                <div className="flex items-center gap-2 text-zinc-400">
                    <Clock className="size-4" />
                    <p className="text-2xs leading-3">{plan.source}</p>
                </div>
            )}
        </div>
    );
}

export function PreferenceGroup({
    title,
    items,
}: {
    title: string;
    items: PreferenceItem[];
}) {
    return (
        <div className="flex flex-col gap-2">
            <p className="heading-6 text-zinc-900">{title}</p>
            {items.map((item) => (
                <div key={item.title} className="flex items-start gap-3 py-1">
                    <SentimentIcon sentiment={item.sentiment} />
                    <div className="flex min-w-0 flex-col gap-0.5">
                        <p className="heading-5 text-zinc-900">{item.title}</p>
                        <p className="text-2xs text-zinc-500 leading-3">
                            {item.description}
                        </p>
                    </div>
                </div>
            ))}
        </div>
    );
}

export function HighlightBanner({ title }: { title: string }) {
    return (
        <Alert variant="warning" className="flex-col bg-amber-100">
            <AlertTitle className="heading-5 text-amber-950">{title}</AlertTitle>
        </Alert>
    );
}

export function AssigneeRow({
    name,
    agentId,
    onEdit,
}: {
    name: string;
    agentId: string;
    onEdit?: () => void;
}) {
    return (
        <div className="flex items-center gap-2">
            <PersonAvatar name={name} kind="agent" id={agentId} />
            <p className="min-w-0 flex-1 text-sm text-zinc-900 leading-label">
                Assigned to {name}
            </p>
            <EditButton label="Edit assignment" onClick={onEdit} />
        </div>
    );
}

export function CardCopyButton({
    brand,
    last4,
    expiry,
}: {
    brand: string;
    last4: string;
    expiry: string;
}) {
    return (
        <Button
            variant="ghost"
            className="h-auto w-full justify-start gap-2 rounded-md px-0 py-1"
            onClick={() => {
                void navigator.clipboard.writeText(`${brand} - ${last4}`);
            }}
        >
            <Copy className="size-4 text-zinc-400" />
            <span className="text-xs text-zinc-900 leading-4">{last4}</span>
            <span className="text-2xs text-zinc-500 leading-3">{brand}</span>
            <Badge variant="secondary" size="sm">
                {expiry}
            </Badge>
        </Button>
    );
}

function SentimentIcon({ sentiment }: { sentiment: PreferenceItem["sentiment"] }) {
    switch (sentiment) {
        case "up":
            return <ThumbsUp className="size-4 shrink-0 text-emerald-700" />;
        case "down":
            return <ThumbsDown className="size-4 shrink-0 text-red-700" />;
        case "love":
            return <Heart className="size-4 shrink-0 text-blue-700" />;
        default: {
            const _exhaustive: never = sentiment;
            return _exhaustive;
        }
    }
}
